import { Injectable } from '@angular/core'

import { FundRuleName } from './fund-rule-name'
import {
  CashEquivalentMuniRule,
  CashEquivalentStableValueRule,
  CashEquivalentTaxableRule,
} from './fund-rules/cash-equivalent-fund-rule'
import {
  EquityBlendRule,
  EquityDevRule,
  EquityEmergingRule,
  EquityGlobalRule,
  EquityGrowthRule,
  EquityINTRule,
  EquityLargeRule,
  EquityMidRule,
  EquityNonUSRule,
  EquitySmallRule,
  EquityStyleActiveRule,
  EquityStyleIndexRule,
  EquityUSMarketRule,
  EquityValueRule,
} from './fund-rules/equity-fund-rule'
import {
  FixedIncomeBelowInvGradeRule,
  FixedIncomeDevRule,
  FixedIncomeEmergingRule,
  FixedIncomeGlobalRule,
  FixedIncomeIntermediateRule,
  FixedIncomeIntlRule,
  FixedIncomeInvGradeCorpRule,
  FixedIncomeLongRule,
  FixedIncomeMultiSectorRule,
  FixedIncomeMuniRule,
  FixedIncomeNonUSRule,
  FixedIncomeShortRule,
  FixedIncomeStyleActiveRule,
  FixedIncomeStyleIndexRule,
  FixedIncomeTreasuryAgencyRule,
  FixedIncomeUSRule,
} from './fund-rules/fixed-income-fund-rule'
import {
  MultiAssetBalanceRule,
  MultiAssetINTRule,
  MultiAssetTargetAllocRule,
  MultiAssetTargetDateRule,
  MultiAssetUSMarketRule,
} from './fund-rules/multi-asset-fund-rule'
import {
  ProductTypeCollectiveTrustRule,
  ProductTypeETFRule,
  ProductTypeMutualFundRule,
} from './fund-rules/product-type-fund-rule'
import {
  DividendStrategyRule,
  ESGStrategyRule,
  FactorDrivenStrategyRule,
  SectorAndSpecialityRule,
} from './fund-rules/strategy-fund-rule'
import { IFundRule } from './ifund-rule'

@Injectable({
  providedIn: 'root',
})
export class FundRuleFactory {
  private fundRuleMap: Map<FundRuleName, IFundRule> = new Map<
    FundRuleName,
    IFundRule
  >()

  constructor() {
    // Product Type
    this.fundRuleMapForProductType()
    // Equity
    this.fundRuleMapForEquity()
    // Fixed Income
    this.fundRuleMapForFixedIncome()
    // Multi Asset
    this.fundRuleMapForMultiAsset()
    // Cash Equivalent
    this.fundRuleMapForCashEquivalent()
    // Strategy
    this.fundRuleMapForStrategy()
  }

  private fundRuleMapForProductType() {
    this.fundRuleMap.set(
      FundRuleName.PRODUCT_TYPE_COLLECTIVE_TRUST,
      new ProductTypeCollectiveTrustRule(),
    )
    this.fundRuleMap.set(
      FundRuleName.PRODUCT_TYPE_MUTUAL_FUND,
      new ProductTypeMutualFundRule(),
    )
    this.fundRuleMap.set(
      FundRuleName.PRODUCT_TYPE_ETF,
      new ProductTypeETFRule(),
    )
  }

  private fundRuleMapForEquity() {
    this.fundRuleMap.set(FundRuleName.EQUITY_US, new EquityUSMarketRule())
    this.fundRuleMap.set(FundRuleName.EQUITY_LARGE, new EquityLargeRule())
    this.fundRuleMap.set(FundRuleName.EQUITY_MID, new EquityMidRule())
    this.fundRuleMap.set(FundRuleName.EQUITY_SMALL, new EquitySmallRule())
    this.fundRuleMap.set(FundRuleName.EQUITY_VALUE, new EquityValueRule())
    this.fundRuleMap.set(FundRuleName.EQUITY_BLEND, new EquityBlendRule())
    this.fundRuleMap.set(FundRuleName.EQUITY_GROWTH, new EquityGrowthRule())
    this.fundRuleMap.set(FundRuleName.EQUITY_INTERNATIONAL, new EquityINTRule())
    this.fundRuleMap.set(FundRuleName.EQUITY_DEVELOPED, new EquityDevRule())
    this.fundRuleMap.set(FundRuleName.EQUITY_EMERGING, new EquityEmergingRule())
    this.fundRuleMap.set(FundRuleName.EQUITY_GLOBAL, new EquityGlobalRule())
    this.fundRuleMap.set(FundRuleName.EQUITY_NON_US, new EquityNonUSRule())
    this.fundRuleMap.set(FundRuleName.EQUITY_INDEX, new EquityStyleIndexRule())
    this.fundRuleMap.set(
      FundRuleName.EQUITY_ACTIVE,
      new EquityStyleActiveRule(),
    )
  }

  // eslint-disable-next-line max-statements
  private fundRuleMapForFixedIncome() {
    this.fundRuleMap.set(FundRuleName.FI_US, new FixedIncomeUSRule())
    this.fundRuleMap.set(
      FundRuleName.FI_INTERNATIONAL,
      new FixedIncomeIntlRule(),
    )
    this.fundRuleMap.set(FundRuleName.FI_DEVELOPED, new FixedIncomeDevRule())
    this.fundRuleMap.set(
      FundRuleName.FI_EMERGING,
      new FixedIncomeEmergingRule(),
    )
    this.fundRuleMap.set(FundRuleName.FI_GLOBAL, new FixedIncomeGlobalRule())
    this.fundRuleMap.set(FundRuleName.FI_NON_US, new FixedIncomeNonUSRule())
    this.fundRuleMap.set(FundRuleName.FI_INDEX, new FixedIncomeStyleIndexRule())
    this.fundRuleMap.set(
      FundRuleName.FI_ACTIVE,
      new FixedIncomeStyleActiveRule(),
    )
    this.fundRuleMap.set(FundRuleName.FI_LONG, new FixedIncomeLongRule())
    this.fundRuleMap.set(FundRuleName.FI_SHORT, new FixedIncomeShortRule())
    this.fundRuleMap.set(
      FundRuleName.FI_INTERMEDIATE,
      new FixedIncomeIntermediateRule(),
    )
    this.fundRuleMap.set(
      FundRuleName.FI_BELOW_INV_GRADE,
      new FixedIncomeBelowInvGradeRule(),
    )
    this.fundRuleMap.set(
      FundRuleName.FI_INV_GRADE_CORP,
      new FixedIncomeInvGradeCorpRule(),
    )
    this.fundRuleMap.set(FundRuleName.FI_MUNI, new FixedIncomeMuniRule())
    this.fundRuleMap.set(
      FundRuleName.FI_MULTI_SECTOR,
      new FixedIncomeMultiSectorRule(),
    )
    this.fundRuleMap.set(
      FundRuleName.FI_TREASURY_AGENCY,
      new FixedIncomeTreasuryAgencyRule(),
    )
  }

  private fundRuleMapForMultiAsset() {
    this.fundRuleMap.set(
      FundRuleName.MULTI_ASSET_TARGET_DATE,
      new MultiAssetTargetDateRule(),
    )
    this.fundRuleMap.set(
      FundRuleName.MULTI_ASSET_BALANCE,
      new MultiAssetBalanceRule(),
    )
    this.fundRuleMap.set(
      FundRuleName.MULTI_ASSET_TARGET_ALLOCATION,
      new MultiAssetTargetAllocRule(),
    )
    this.fundRuleMap.set(
      FundRuleName.MULTI_ASSET_US,
      new MultiAssetUSMarketRule(),
    )
    this.fundRuleMap.set(FundRuleName.MULTI_ASSET_INTL, new MultiAssetINTRule())
  }

  private fundRuleMapForCashEquivalent() {
    this.fundRuleMap.set(
      FundRuleName.CASH_EQUIVALENTS_MUNI,
      new CashEquivalentMuniRule(),
    )
    this.fundRuleMap.set(
      FundRuleName.CASH_EQUIVALENTS_TAXABLE,
      new CashEquivalentTaxableRule(),
    )
    this.fundRuleMap.set(
      FundRuleName.CASH_EQUIVALENTS_STABLE_VALUE,
      new CashEquivalentStableValueRule(),
    )
  }

  private fundRuleMapForStrategy() {
    this.fundRuleMap.set(
      FundRuleName.DIVIDEND_STRATEGY,
      new DividendStrategyRule(),
    )
    this.fundRuleMap.set(FundRuleName.ESG_STRATEGY, new ESGStrategyRule())
    this.fundRuleMap.set(
      FundRuleName.FACTOR_DRIVEN_STRATEGY,
      new FactorDrivenStrategyRule(),
    )
    this.fundRuleMap.set(
      FundRuleName.SECTOR_SPECIALITY_STRATEGY,
      new SectorAndSpecialityRule(),
    )
  }

  getFundRule(fundRuleName: FundRuleName): IFundRule {
    return this.fundRuleMap.get(fundRuleName)
  }
}
